// Generated from scripts/banner.ts. Rebuild with pnpm scripts:build.
"use strict";
/**
 * The <wontio-banner> element around each banner of a banner slot (snippets/banner-slot.liquid), inlined by the
 * slot before its banners parse (no imports or exports, block scope), so a banner that must not show is hidden
 * before it paints and never shifts the page.
 *
 * - Closed banners: a close press fades the banner out over 200 ms (opacity only), hides it and remembers the
 *   choice for 24 hours in localStorage under the banner's handle. Focus moves to the next control after it.
 * - Schedule: the server shows a banner inside its start and end; a cached page can outlive either, so the element
 *   checks the window again (data-starts, data-ends, seconds since the epoch).
 * - Cart bounds: data-min-cart and data-max-cart are the least and greatest cart total in the store currency's minor
 *   units, data-cart-total the cart total in the shopper's currency. The element converts the bounds at the
 *   storefront's rate and shows the banner while the total is within them; banner-cart.js rewrites data-cart-total
 *   when the cart changes.
 * - Shipping countdown: a [countdown] shortcode in the text renders a [data-banner-countdown] span with its arguments
 *   (see #startCountdowns); the element ticks it every second while connected.
 * - Discount code: a press copies the code, swaps the copy icon for a check for 1.8 s and announces the result.
 *   When the clipboard refuses, the code is selected and the failure text shows next to it.
 * In the theme editor the element neither reads nor writes storage.
 */
{
    const STORAGE_KEY = 'wontio:banners-dismissed';
    const DISMISS_MS = 24 * 60 * 60 * 1000;
    /** Longer than the 200 ms fade, so a missing `transitionend` still hides the banner. */
    const FADE_FALLBACK_MS = 400;
    const COPIED_MS = 1800;
    const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const readDismissals = () => {
        try {
            const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}');
            if (!parsed || typeof parsed !== 'object')
                return {};
            const now = Date.now();
            const live = {};
            for (const [handle, expires] of Object.entries(parsed)) {
                if (typeof expires === 'number' && expires > now)
                    live[handle] = expires;
            }
            return live;
        }
        catch {
            return {};
        }
    };
    const remember = (handle) => {
        try {
            const dismissals = readDismissals();
            dismissals[handle] = Date.now() + DISMISS_MS;
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(dismissals));
        }
        catch {
            // Blocked storage keeps the choice for this page only.
        }
    };
    /** The storefront's conversion rate from the store currency to the shopper's, 1 when unknown. */
    const currencyRate = () => {
        const shopify = window.Shopify;
        const rate = Number(shopify?.currency?.rate);
        return Number.isFinite(rate) && rate > 0 ? rate : 1;
    };
    const numberAttribute = (element, name) => {
        const value = element.getAttribute(name);
        if (value === null || value === '')
            return null;
        const number = Number(value);
        return Number.isFinite(number) ? number : null;
    };
    /** The [countdown] arguments: key=value, key="value" or key='value', numbers and true/false coerced. */
    const parseArguments = (source) => {
        const result = {};
        for (const match of source.matchAll(/([a-z_]+)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"']+))/gi)) {
            const key = match[1];
            if (!key)
                continue;
            const raw = match[2] ?? match[3] ?? match[4] ?? '';
            const bare = match[4] !== undefined;
            if (bare && /^-?\d+(\.\d+)?$/.test(raw))
                result[key.toLowerCase()] = Number(raw);
            else if (bare && /^(true|false)$/i.test(raw))
                result[key.toLowerCase()] = raw.toLowerCase() === 'true';
            else
                result[key.toLowerCase()] = raw;
        }
        return result;
    };
    /** The wall clock in an IANA time zone, or null when the zone is unknown. */
    const wallClock = (zone, at) => {
        try {
            const parts = new Intl.DateTimeFormat('en-US', {
                timeZone: zone,
                weekday: 'short',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hourCycle: 'h23',
            }).formatToParts(at);
            const value = (type) => parts.find((part) => part.type === type)?.value ?? '';
            const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(value('weekday'));
            const secondOfDay = Number(value('hour')) * 3600 + Number(value('minute')) * 60 + Number(value('second'));
            return weekday < 0 || !Number.isFinite(secondOfDay) ? null : { weekday, secondOfDay };
        }
        catch {
            return null;
        }
    };
    const pad = (value) => String(value).padStart(2, '0');
    /**
     * One shipping countdown: the time left until the next order cutoff (hour, in the zone tz), skipping Saturday and
     * Sunday unless skip_weekends=false. Within hide_threshold_hours (default 20) it reads "<label> <prefix> HH:MM:SS",
     * hours counted in total; further away only the label shows (the shipping day). Without a known zone it hides.
     * Labels: label_today, label_tomorrow and label_monday (any later day) override the translated defaults; layout
     * inline, stacked (label and time on two lines) or compact (no prefix); align left, center or right.
     */
    const renderCountdown = (node, now) => {
        const args = parseArguments(node.dataset.args ?? '');
        const zone = typeof args.tz === 'string' && args.tz ? args.tz : node.dataset.defaultTz ?? '';
        const clock = zone ? wallClock(zone, now) : null;
        if (!clock) {
            node.hidden = true;
            return;
        }
        const hour = typeof args.hour === 'number' ? args.hour : 9;
        const skipWeekends = args.skip_weekends !== false;
        const threshold = (typeof args.hide_threshold_hours === 'number' ? args.hide_threshold_hours : 20) * 3600;
        const cutoff = Math.round(hour * 3600);
        let daysAhead = 0;
        let weekday = clock.weekday;
        const shipsOn = (day) => !skipWeekends || (day !== 0 && day !== 6);
        if (!shipsOn(weekday) || clock.secondOfDay >= cutoff) {
            do {
                daysAhead += 1;
                weekday = (weekday + 1) % 7;
            } while (!shipsOn(weekday) && daysAhead < 7);
        }
        const remaining = daysAhead * 86_400 + cutoff - clock.secondOfDay;
        const dayName = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { weekday: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2024, 0, 7 + weekday)));
        const label = daysAhead === 0
            ? String(args.label_today ?? node.dataset.labelToday ?? '')
            : daysAhead === 1
                ? String(args.label_tomorrow ?? node.dataset.labelTomorrow ?? '')
                : String(args.label_monday ?? (node.dataset.labelNext ?? '').replace('__DAY__', dayName));
        const prefix = args.layout === 'compact' ? '' : String(args.prefix ?? node.dataset.prefix ?? '');
        const align = args.align === 'center' || args.align === 'right' ? args.align : 'left';
        node.style.textAlign = align === 'left' ? 'start' : align === 'right' ? 'end' : 'center';
        node.style.display = args.layout === 'stacked' ? 'block' : '';
        const labelNode = node.querySelector('[data-countdown-label]');
        const timeNode = node.querySelector('[data-countdown-time]');
        if (!labelNode || !timeNode)
            return;
        node.hidden = false;
        if (remaining > threshold) {
            labelNode.textContent = label;
            timeNode.textContent = '';
            timeNode.hidden = true;
            return;
        }
        const hours = Math.floor(remaining / 3600);
        const minutes = Math.floor((remaining % 3600) / 60);
        const seconds = remaining % 60;
        labelNode.textContent = [label, prefix].filter(Boolean).join(' ');
        timeNode.hidden = false;
        timeNode.style.display = args.layout === 'stacked' ? 'block' : '';
        timeNode.textContent = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    };
    class WontioBanner extends HTMLElement {
        static observedAttributes = ['data-cart-total'];
        #lifetime;
        #fallback;
        #copied;
        #dismissed = false;
        get #designMode() {
            return this.hasAttribute('data-design-mode');
        }
        connectedCallback() {
            const handle = this.dataset.banner ?? '';
            if (!this.#designMode && this.hasAttribute('data-dismissible') && handle && handle in readDismissals()) {
                this.#dismissed = true;
            }
            this.#update(false);
            this.#lifetime = new AbortController();
            this.addEventListener('click', this.#onClick, { signal: this.#lifetime.signal });
            // The element is defined before its children parse; countdowns start once they exist.
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', this.#startCountdowns, { once: true, signal: this.#lifetime.signal });
            }
            else {
                this.#startCountdowns();
            }
        }
        disconnectedCallback() {
            this.#lifetime?.abort();
            this.#lifetime = undefined;
            window.clearTimeout(this.#fallback);
            window.clearTimeout(this.#copied);
            window.clearInterval(this.#ticker);
            this.#ticker = undefined;
        }
        #ticker;
        #startCountdowns = () => {
            const nodes = Array.from(this.querySelectorAll('[data-banner-countdown]'));
            if (!nodes.length || this.#ticker !== undefined)
                return;
            const tick = () => {
                const now = new Date();
                for (const node of nodes)
                    renderCountdown(node, now);
            };
            tick();
            this.#ticker = window.setInterval(tick, 1000);
        };
        attributeChangedCallback() {
            if (this.#lifetime)
                this.#update(true);
        }
        /** Whether the banner applies now: not closed, inside its window and, with a minimum, at or above it. */
        #applies() {
            if (this.#dismissed)
                return false;
            const now = Date.now() / 1000;
            const starts = numberAttribute(this, 'data-starts');
            const ends = numberAttribute(this, 'data-ends');
            if (starts !== null && now < starts)
                return false;
            if (ends !== null && now >= ends)
                return false;
            const minimum = numberAttribute(this, 'data-min-cart');
            const maximum = numberAttribute(this, 'data-max-cart');
            const total = numberAttribute(this, 'data-cart-total') ?? 0;
            if (minimum !== null && minimum > 0 && total < Math.round(minimum * currencyRate()))
                return false;
            if (maximum !== null && total > Math.round(maximum * currencyRate()))
                return false;
            return true;
        }
        /** Shows or hides the banner; a banner that appears after the page loaded fades in (data-revealed). */
        #update(animate) {
            if (this.hasAttribute('data-dismissing'))
                return;
            const applies = this.#applies();
            if (applies && this.hidden) {
                if (animate)
                    this.setAttribute('data-revealed', '');
                this.hidden = false;
            }
            else if (!applies && !this.hidden) {
                this.hidden = true;
            }
        }
        #onClick = (event) => {
            if (!(event.target instanceof Element))
                return;
            if (event.target.closest('[data-banner-dismiss]'))
                this.#dismiss();
            const code = event.target.closest('[data-banner-code]');
            if (code)
                void this.#copy(code);
        };
        #dismiss() {
            if (this.hasAttribute('data-dismissing'))
                return;
            const handle = this.dataset.banner;
            if (handle && !this.#designMode)
                remember(handle);
            this.#dismissed = true;
            this.setAttribute('data-dismissing', '');
            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                this.#collapse();
                return;
            }
            this.addEventListener('transitionend', this.#onTransitionEnd, { signal: this.#lifetime?.signal });
            this.#fallback = window.setTimeout(this.#collapse, FADE_FALLBACK_MS);
        }
        #onTransitionEnd = (event) => {
            if (event.target === this && event.propertyName === 'opacity')
                this.#collapse();
        };
        #collapse = () => {
            window.clearTimeout(this.#fallback);
            if (this.hidden)
                return;
            const hadFocus = this.contains(document.activeElement);
            this.hidden = true;
            this.removeAttribute('data-dismissing');
            if (hadFocus)
                this.#moveFocus();
        };
        /** The focused close button is gone: focus the next control after this banner, as a keyboard user expects. */
        #moveFocus() {
            const controls = Array.from(document.querySelectorAll(FOCUSABLE));
            for (const target of controls) {
                // The banner's own controls are hidden but can still hold focus until the browser's next focus fixup.
                if (this.contains(target) || !(this.compareDocumentPosition(target) & Node.DOCUMENT_POSITION_FOLLOWING))
                    continue;
                target.focus({ preventScroll: true });
                if (document.activeElement === target)
                    return;
            }
        }
        async #copy(button) {
            const code = button.dataset.bannerCode ?? '';
            const status = button.parentElement?.querySelector('[data-banner-status]');
            if (!code || !status)
                return;
            try {
                await navigator.clipboard.writeText(code);
                status.classList.add('visually-hidden');
                status.textContent = status.dataset.copied ?? '';
                button.querySelector('[data-banner-copy-icon]')?.classList.add('tw:hidden');
                button.querySelector('[data-banner-copied-icon]')?.classList.remove('tw:hidden');
                window.clearTimeout(this.#copied);
                this.#copied = window.setTimeout(() => {
                    button.querySelector('[data-banner-copy-icon]')?.classList.remove('tw:hidden');
                    button.querySelector('[data-banner-copied-icon]')?.classList.add('tw:hidden');
                    status.textContent = '';
                }, COPIED_MS);
            }
            catch {
                const selection = window.getSelection();
                const range = document.createRange();
                range.selectNodeContents(button.firstElementChild ?? button);
                selection?.removeAllRanges();
                selection?.addRange(range);
                status.classList.remove('visually-hidden');
                status.textContent = status.dataset.failed ?? '';
            }
        }
    }
    if (!customElements.get('wontio-banner'))
        customElements.define('wontio-banner', WontioBanner);
}
